import React, { type FC, type RefObject, useState } from 'react';
import { type Toast } from 'primereact/toast';
import type SharedChart from '../models/SharedChartModel';
import { isoToFormattedString } from '../utils/date';
import api from '../services/api';

type ChartResultProps = {
    cycleNumber: number;
    chartData: SharedChart;
    friendId: number;
    toast: RefObject<Toast>;
    hasInstructorNotesPermission: boolean;
};

const ChartResult: FC<ChartResultProps> = ({
    cycleNumber,
    chartData,
    friendId,
    toast,
    hasInstructorNotesPermission,
}) => {
    const startDate = isoToFormattedString(chartData.chartDate);

    const [instructorNotes, setInstructorNotes] = useState(
        chartData.instructorNotes,
    );
    const [internalNotes, setInternalNotes] = useState(chartData.internalNotes);

    const updateNotes = async (internal: boolean) => {
        const { success, error } = await api.setNotes(
            friendId,
            chartData.chartDate,
            internal ? undefined : instructorNotes,
            internal ? internalNotes : undefined,
        );
        if (success) {
            toast.current?.show({
                severity: 'success',
                summary: `${internal ? 'Internal' : 'Instructor'} Notes Saved`,
            });
        } else {
            toast.current?.show({
                severity: 'warn',
                summary: 'Update Failed',
                detail: error ?? 'unknown error',
            });
        }
    };

    const updateInstructorNotes = async () => {
        await updateNotes(false);
    };

    const updateInternalNotes = async () => {
        await updateNotes(true);
    };

    return (
        <div className="w-[1125px] max-w-full">
            <div className="text-2xl text-primary underline mb-2">
                Cycle {cycleNumber}: {startDate}
            </div>
            <img
                className="w-[1125px] max-w-full"
                crossOrigin="anonymous"
                src={`${chartData.chartUrl}&html=false`}
            />
            <div className="flex flex-row gap-8 pt-8">
                <a
                    className="btn btn-primary"
                    href={`/stats?id=${friendId}&start=${chartData.chartDate}`}
                >
                    Stats
                </a>
                <a
                    className="btn btn-primary"
                    href={`/compare?id=${friendId}&start=${chartData.chartDate}`}
                >
                    Compare
                </a>
            </div>
            <div className="pb-8 border-b border-primary mb-8">
                <div className="text-lg text-primary mt-8">User Notes</div>
                <textarea
                    disabled
                    className="w-[1125px] h-32 max-w-full block p-2 text-primary bg-white overflow-y-scroll"
                    value={chartData.userNotes || 'None'}
                />
                <div className="text-lg text-primary mt-8">
                    Instructor Notes
                </div>
                <textarea
                    disabled={!hasInstructorNotesPermission}
                    className="w-[1125px] h-32 max-w-full block p-2 text-primary bg-white overflow-y-scroll"
                    value={instructorNotes ?? ''}
                    onChange={(event) => {
                        setInstructorNotes(event.target.value);
                    }}
                />
                {hasInstructorNotesPermission && (
                    <button
                        type="button"
                        className="btn btn-primary mt-2"
                        onClick={updateInstructorNotes}
                    >
                        Save
                    </button>
                )}
                {internalNotes !== undefined && (
                    <>
                        <div className="text-lg text-primary mt-8">
                            Internal Notes
                        </div>
                        <textarea
                            className="w-[1125px] h-32 max-w-full block p-2 text-primary bg-white overflow-y-scroll"
                            value={internalNotes}
                            onChange={(event) => {
                                setInternalNotes(event.target.value);
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-primary mt-2"
                            onClick={updateInternalNotes}
                        >
                            Save
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChartResult;
